import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { CheckCircle, Cancel, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'all'];

function buildDefaultNotes(type, row) {
  if (!row?.existingAccount) return '';
  const ex = row.existingAccount;
  const exLine = `Existing account: ${ex.company_name} (Reference: ${ex.account_code || '—'}, ID: ${ex.id}).`;
  if (type === 'approve') {
    return `${exLine} Approving creation of a separate account for requested name: "${row.requested_company_name}". `;
  }
  return `${exLine} Rejecting duplicate account request for: "${row.requested_company_name}". `;
}

export default function AccountDuplicateRequests() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [decisionDialog, setDecisionDialog] = useState({ open: false, type: 'approve', row: null });
  const [notes, setNotes] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, statResp] = await Promise.all([
        api.get('/admin/account-duplicate-requests', { params: { status, search, page: 1, limit: 100 } }),
        api.get('/admin/account-duplicate-requests/stats')
      ]);
      setRows(listResp.data?.data?.requests || []);
      setStats(statResp.data?.data || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load account duplicate requests');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openDecision = (type, row) => {
    setDecisionDialog({ open: true, type, row });
    setNotes(buildDefaultNotes(type, row));
  };

  const submitDecision = async () => {
    const row = decisionDialog.row;
    if (!row) return;
    const action = decisionDialog.type === 'approve' ? 'approve' : 'reject';
    setBusyId(row.id);
    try {
      await api.post(`/admin/account-duplicate-requests/${row.id}/${action}`, {
        notes: notes.trim() || undefined
      });
      toast.success(
        action === 'approve'
          ? 'Request approved and duplicate account created.'
          : 'Request rejected.'
      );
      setDecisionDialog({ open: false, type: 'approve', row: null });
      setNotes('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Account Duplicate Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Hard-blocked duplicate company names are queued here for Admin/Manager review.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={load} disabled={loading} variant="outlined">
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}><CardContent><Typography variant="h4">{stats.pending || 0}</Typography><Typography variant="caption">Pending</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}><CardContent><Typography variant="h4">{stats.approved || 0}</Typography><Typography variant="caption">Approved</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}><CardContent><Typography variant="h4">{stats.rejected || 0}</Typography><Typography variant="caption">Rejected</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}><CardContent><Typography variant="h4">{stats.total || 0}</Typography><Typography variant="caption">Total</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Select size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 180 }}>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by requested company name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Requested company</TableCell>
              <TableCell>Existing account</TableCell>
              <TableCell>New account (if approved)</TableCell>
              <TableCell>Requested by</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const pending = r.status === 'pending';
              return (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.requested_company_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.normalized_company_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.existingAccount?.company_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.existingAccount?.account_code || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.createdAccount ? (
                      <>
                        <Typography variant="body2">{r.createdAccount.company_name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace">
                          {r.createdAccount.account_code}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {r.submitter ? `${r.submitter.first_name || ''} ${r.submitter.last_name || ''}`.trim() : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{r.submitter?.email || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={String(r.status || '').toUpperCase()}
                      color={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'error' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    {pending ? (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => openDecision('approve', r)}
                          disabled={busyId === r.id}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => openDecision('reject', r)}
                          disabled={busyId === r.id}
                        >
                          Reject
                        </Button>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {r.reviewer ? `Reviewed by ${r.reviewer.first_name || ''}` : 'Processed'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mt: 2 }}>No duplicate-account requests for this filter.</Alert>
      ) : null}

      <Dialog open={decisionDialog.open} onClose={() => setDecisionDialog({ open: false, type: 'approve', row: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{decisionDialog.type === 'approve' ? 'Approve duplicate account request' : 'Reject duplicate account request'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Requested company: <strong>{decisionDialog.row?.requested_company_name || '—'}</strong>
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Decision notes"
            placeholder="Notes are pre-filled with the existing account. Add details if needed."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText="The existing account is included by default. You may append additional comments before approving or rejecting."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionDialog({ open: false, type: 'approve', row: null })}>Cancel</Button>
          <Button onClick={submitDecision} variant="contained" color={decisionDialog.type === 'approve' ? 'success' : 'error'}>
            {decisionDialog.type === 'approve' ? 'Approve & create account' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

